import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Plus, Download, FileText, Trash2 } from 'lucide-react';
import { EditableCell, SortableHeader, downloadExport } from './PricingShared';

const VehiclesTab = ({ vehicles, sortConfig, onSort, selectedMonth, onDeleteVehicle, onInlineEdit, onShowForm, API }) => {
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editingField, setEditingField] = useState(null);

  const handleStartEdit = (vehicleId, field) => { setEditingVehicle(vehicleId); setEditingField(field); };
  const handleSave = (vehicleId, field, value) => {
    onInlineEdit(vehicleId, field, value);
    setEditingVehicle(null);
    setEditingField(null);
  };

  return (
    <Card className="border-gray-200 bg-white">
      <div className="p-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold">Vehicle ROI Tracker</h3>
          <p className="text-sm text-gray-500">Click any cell to edit inline</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadExport(`${API}/api/pricing/export/vehicles.csv?month=${encodeURIComponent(selectedMonth)}`)} data-testid="export-vehicles-csv">
            <Download className="h-4 w-4 mr-1" />CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadExport(`${API}/api/pricing/export/vehicles.pdf?month=${encodeURIComponent(selectedMonth)}`)} data-testid="export-vehicles-pdf">
            <FileText className="h-4 w-4 mr-1" />PDF
          </Button>
          <Button onClick={onShowForm} className="bg-red-600 hover:bg-red-700" data-testid="add-vehicle-btn">
            <Plus className="h-4 w-4 mr-2" />Add Vehicle
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="vehicles-table">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader label="Vehicle" field="vehicle_name" sortConfig={sortConfig} onSort={onSort} className="text-left" />
              <SortableHeader label="Category" field="category" sortConfig={sortConfig} onSort={onSort} className="text-left" />
              <SortableHeader label="Ownership" field="ownership_default" sortConfig={sortConfig} onSort={onSort} className="text-left" />
              <SortableHeader label="Retail/Hr" field="retail_hourly" sortConfig={sortConfig} onSort={onSort} className="text-right" />
              <SortableHeader label="Affiliate/Hr" field="affiliate_hourly" sortConfig={sortConfig} onSort={onSort} className="text-right" />
              <SortableHeader label="Cost Basis" field="cost_basis" sortConfig={sortConfig} onSort={onSort} className="text-right" />
              <SortableHeader label="Monthly Rev" field="monthly_revenue" sortConfig={sortConfig} onSort={onSort} className="text-right" />
              <SortableHeader label="Monthly Profit" field="monthly_profit" sortConfig={sortConfig} onSort={onSort} className="text-right" />
              <SortableHeader label="Annual Profit" field="annual_profit" sortConfig={sortConfig} onSort={onSort} className="text-right" />
              <SortableHeader label="ROI %" field="roi_percent" sortConfig={sortConfig} onSort={onSort} className="text-right" />
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 text-center w-16">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {vehicles.map((vehicle) => (
              <tr key={vehicle.vehicle_id} className="hover:bg-gray-50 transition-colors" data-testid={`vehicle-row-${vehicle.vehicle_id}`}>
                <td className="py-2 px-4 text-sm font-semibold">
                  <EditableCell value={vehicle.vehicle_name} field="vehicle_name" type="text" isEditing={editingVehicle === vehicle.vehicle_id && editingField === 'vehicle_name'} onStartEdit={(f) => handleStartEdit(vehicle.vehicle_id, f)} onSave={(f, v) => handleSave(vehicle.vehicle_id, f, v)} />
                </td>
                <td className="py-2 px-4 text-sm capitalize">
                  <EditableCell value={vehicle.category} field="category" type="select" options={[{value:'exotic_sedan',label:'Exotic Sedan'},{value:'exotic_suv',label:'Exotic SUV'},{value:'concierge',label:'Concierge'},{value:'group',label:'Group'}]} isEditing={editingVehicle === vehicle.vehicle_id && editingField === 'category'} onStartEdit={(f) => handleStartEdit(vehicle.vehicle_id, f)} onSave={(f, v) => handleSave(vehicle.vehicle_id, f, v)} />
                </td>
                <td className="py-2 px-4 text-sm capitalize">
                  <EditableCell value={vehicle.ownership_default} field="ownership_default" type="select" options={[{value:'owned',label:'Owned'},{value:'rental',label:'Rental'}]} isEditing={editingVehicle === vehicle.vehicle_id && editingField === 'ownership_default'} onStartEdit={(f) => handleStartEdit(vehicle.vehicle_id, f)} onSave={(f, v) => handleSave(vehicle.vehicle_id, f, v)} />
                </td>
                <td className="py-2 px-4 text-sm text-right">
                  <EditableCell value={vehicle.retail_hourly} field="retail_hourly" type="number" isEditing={editingVehicle === vehicle.vehicle_id && editingField === 'retail_hourly'} onStartEdit={(f) => handleStartEdit(vehicle.vehicle_id, f)} onSave={(f, v) => handleSave(vehicle.vehicle_id, f, v)} />
                </td>
                <td className="py-2 px-4 text-sm text-right">
                  <EditableCell value={vehicle.affiliate_hourly} field="affiliate_hourly" type="number" isEditing={editingVehicle === vehicle.vehicle_id && editingField === 'affiliate_hourly'} onStartEdit={(f) => handleStartEdit(vehicle.vehicle_id, f)} onSave={(f, v) => handleSave(vehicle.vehicle_id, f, v)} />
                </td>
                <td className="py-2 px-4 text-sm text-right">
                  <EditableCell value={vehicle.cost_basis} field="cost_basis" type="number" isEditing={editingVehicle === vehicle.vehicle_id && editingField === 'cost_basis'} onStartEdit={(f) => handleStartEdit(vehicle.vehicle_id, f)} onSave={(f, v) => handleSave(vehicle.vehicle_id, f, v)} />
                </td>
                <td className="py-2 px-4 text-sm text-right font-semibold text-green-700">${vehicle.monthly_revenue}</td>
                <td className="py-2 px-4 text-sm text-right font-semibold text-blue-700">${vehicle.monthly_profit}</td>
                <td className="py-2 px-4 text-sm text-right font-semibold">${vehicle.annual_profit}</td>
                <td className="py-2 px-4 text-sm text-right">
                  <span className={`font-bold ${vehicle.roi_percent > 20 ? 'text-green-600' : vehicle.roi_percent > 10 ? 'text-yellow-600' : 'text-red-600'}`}>{vehicle.roi_percent}%</span>
                </td>
                <td className="py-2 px-4 text-center">
                  <Button variant="ghost" size="sm" onClick={() => onDeleteVehicle(vehicle.vehicle_id)} className="hover:bg-red-50" data-testid={`delete-vehicle-${vehicle.vehicle_id}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {vehicles.length === 0 && <div className="text-center py-12 text-gray-500">No vehicles added. Click "Add Vehicle" to create one.</div>}
      </div>
    </Card>
  );
};

export default VehiclesTab;
